import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useGalleryData } from '../../hooks/useGalleryData';

const Pricing: React.FC = () => {
  const { t } = useTranslation();
  const { pricingData, loading } = useGalleryData();

  if (loading) {
    return (
      <section className="py-24 bg-dark-primary">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-pulse">
              <div className="w-24 h-px bg-gold/30 mx-auto mb-8"></div>
              <div className="h-8 bg-dark-surface rounded w-64 mx-auto mb-4"></div>
              <div className="h-4 bg-dark-surface rounded w-96 mx-auto"></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (!pricingData) return null;

  return (
    <section id="pricing" className="py-24 bg-dark-primary relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent"></div>
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section header */}
        <motion.div
          className="text-center mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          {/* Elegant separator */}
          <motion.div
            initial={{ width: 0, opacity: 0 }}
            whileInView={{ width: '120px', opacity: 1 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            viewport={{ once: true }}
            className="h-px bg-gold/50 mx-auto mb-8"
          />

          <h2 className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-light text-cream mb-6 tracking-wide">
            {t('pricing.title')}
          </h2>
          <p className="text-lg text-cream-dark max-w-3xl mx-auto font-light leading-relaxed">
            {t('pricing.subtitle')}
          </p>

          {/* Bottom separator */}
          <motion.div
            initial={{ width: 0, opacity: 0 }}
            whileInView={{ width: '80px', opacity: 1 }}
            transition={{ duration: 1.2, delay: 0.3, ease: "easeOut" }}
            viewport={{ once: true }}
            className="h-px bg-gold/30 mx-auto mt-8"
          />
        </motion.div>

        {/* Pricing Tables */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Canvas Prints */}
          <motion.div
            className="bg-dark-surface/50 border border-dark-border/30 backdrop-blur-sm p-6 sm:p-8"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="text-center mb-8">
              <h3 className="font-playfair text-2xl text-cream mb-2">
                {pricingData.canvasPrints.title}
              </h3>
              <div className="w-16 h-px bg-gold/50 mx-auto"></div>
            </div>

            <div className="space-y-4">
              {pricingData.canvasPrints.sizes.map((size, index) => (
                <motion.div
                  key={size.id}
                  className={`flex items-center justify-between p-4 border transition-all duration-300 ${
                    size.recommended 
                      ? 'border-gold/50 bg-gold/5' 
                      : 'border-dark-border/20 hover:border-gold/30'
                  }`}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="flex items-center">
                    <span className="text-cream font-light">
                      {size.dimensions}
                    </span>
                    {size.recommended && (
                      <span className="ml-3 text-xs bg-gold/20 text-gold px-2 py-1 uppercase tracking-wider">
                        {t('pricing.recommended')}
                      </span>
                    )}
                  </div>
                  <span className="font-playfair text-lg text-gold">
                    {size.price}{pricingData.canvasPrints.currency}
                  </span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Alu-Dibond Prints */}
          <motion.div
            className="bg-dark-surface/50 border border-dark-border/30 backdrop-blur-sm p-6 sm:p-8"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <div className="text-center mb-8">
              <h3 className="font-playfair text-2xl text-cream mb-2">
                {pricingData.aluDibondPrints.title}
              </h3>
              <div className="w-16 h-px bg-gold/50 mx-auto"></div>
            </div>

            <div className="space-y-4">
              {pricingData.aluDibondPrints.sizes.map((size, index) => (
                <motion.div
                  key={size.id}
                  className="flex items-center justify-between p-4 border border-dark-border/20 hover:border-gold/30 transition-all duration-300"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <span className="text-cream font-light">
                    {size.dimensions}
                  </span>
                  <span className="font-playfair text-lg text-gold">
                    {size.price}{pricingData.aluDibondPrints.currency}
                  </span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Shipping Information */}
        <motion.div
          className="mt-16 bg-dark-surface/30 border border-dark-border/20 p-8 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <h4 className="font-playfair text-xl text-cream mb-6">
            {t('pricing.shipping')}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
            <div>
              <p className="text-cream-dark font-light">
                {pricingData.shipping.germany.description}
              </p>
              <p className="text-gold font-medium mt-1">
                {pricingData.shipping.germany.price}{pricingData.shipping.germany.currency}
              </p>
            </div>
            <div>
              <p className="text-cream-dark font-light">
                {pricingData.shipping.international.description}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Contact Section */}
        <motion.div
          className="mt-16 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="w-12 h-px bg-gold/50 mx-auto mb-8"></div>
          <h4 className="font-playfair text-2xl text-cream mb-6">
            {t('pricing.interestedTitle')}
          </h4>
          <p className="text-cream-dark font-light leading-relaxed mb-8 max-w-2xl mx-auto">
            {t('pricing.interestedDescription')}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
            <a
              href="mailto:info@tamerin.tech?subject=Pricing%20Inquiry"
              className="px-8 py-3 border border-gold/30 hover:border-gold hover:bg-gold/5 transition-all duration-300"
            >
              <span className="text-cream font-light tracking-wider uppercase text-sm">
                {t('contact.sendEmail')}
              </span>
            </a>
            <a
              href="https://wa.me/4915907614478?text=Hi%2C%20ich%20interessiere%20mich%20f%C3%BCr%20die%20Preise"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-3 bg-gold/90 hover:bg-gold transition-all duration-300"
            >
              <span className="text-dark-primary font-medium tracking-wider uppercase text-sm">
                {t('contact.openWhatsApp')}
              </span>
            </a>
          </div>
        </motion.div>

        {/* Decorative elements */}
        <div className="absolute top-20 right-4 w-24 h-24 bg-gold/5 rounded-full filter blur-xl animate-float"></div>
        <div className="absolute bottom-20 left-4 w-32 h-32 bg-gold/5 rounded-full filter blur-xl animate-float" style={{ animationDelay: '2s' }}></div>
      </div>
    </section>
  );
};

export default Pricing;